import { getCruiseItems, getBookingCabinSelectionData } from 'checkout/selectors/cruiseSelectors'
import CruiseSessionCountdownMessage from 'components/Common/CruiseSessionCountdownClock/CruiseSessionCountdownMessage'
import { FlightSessionCountdownClock } from 'components/Common/FlightSessionCountdownClock/FlightSessionCountdownClock'
import { shouldRenderCruiseSessionCountdownClock } from 'components/Common/CruiseSessionCountdownClock/utils'
import PageBanner from 'LuxKit/Banners/PageBanner'
import { mediaQueryUp } from 'components/utils/breakpoint'
import React, { useMemo } from 'react'
import { connect } from 'react-redux'
import { useRouteMatch } from 'react-router'
import styled from 'styled-components'
import zIndex from 'styles/tools/z-index'
import { useAppSelector } from 'hooks/reduxHooks'
import { flightSearchInProgress, getNonCharterFlightItems } from 'checkout/selectors/view/flights'
import { shouldRenderFlightSessionCountdownClock } from 'components/Common/FlightSessionCountdownClock/utils'
import { isFlightsPendingToAddToCart } from 'checkout/selectors/navigationSelectors'

const Container = styled.div`
  ${mediaQueryUp.desktop} {
    position: sticky;
    z-index: ${zIndex.stickyHeaderBanner};
    top: ${props => props.theme.layout.headerFloatingOffset};
    right: 0;
    left: 0;
  }

  &:empty {
    display: none;
  }
`

interface MappedStateProps {
  hasCruise: boolean
  hasExpirableFlights: boolean
  hasFlightsPendingToAddToCart: boolean
  isFetchingFlights: boolean
}

/**
 * Contains high-level banners and elements in the checkout.
 *
 * Becomes sticky on desktop and larger devices.
 */
function CheckoutSubheader(props: MappedStateProps) {
  const {
    hasCruise,
    hasExpirableFlights,
    hasFlightsPendingToAddToCart,
    isFetchingFlights
  } = props

  const match = useRouteMatch<{ step: string }>()
  const cabinSelectionData = useAppSelector((state) => getBookingCabinSelectionData(state))

  const showCruiseSessionCountdownClock = useMemo(() => (
    hasCruise &&
    !cabinSelectionData.loading &&
    shouldRenderCruiseSessionCountdownClock(match.params.step)
  ), [hasCruise, cabinSelectionData, match.params.step])

  const showFlightSessionCountdownClock = useMemo(() => (
    !hasFlightsPendingToAddToCart && shouldRenderFlightSessionCountdownClock(hasExpirableFlights) &&
    !isFetchingFlights &&
    match.params.step !== 'lux-plus'
  ), [hasExpirableFlights, hasFlightsPendingToAddToCart, isFetchingFlights, match.params.step])

  return <Container id="checkout-sub-header">
    {showCruiseSessionCountdownClock && <PageBanner
      kind="informational-primary"
      align="center"
    >
      <CruiseSessionCountdownMessage />
    </PageBanner>}
    {showFlightSessionCountdownClock && <PageBanner
      kind="informational-primary"
      align="center"
    >
      <FlightSessionCountdownClock />
    </PageBanner>}
  </Container>
}

export default connect<MappedStateProps, {}, {}, App.State>((state) => {
  return {
    hasCruise: getCruiseItems(state).length > 0,
    hasExpirableFlights: getNonCharterFlightItems(state).length > 0,
    hasFlightsPendingToAddToCart: isFlightsPendingToAddToCart(state),
    isFetchingFlights: flightSearchInProgress(state),
  }
})(CheckoutSubheader)
